export const AREA_MIN_VALUE = 0.01;
export const AREA_MAX_VALUE = 10_000_000;
export const AREA_MAX_FRACTION_DIGITS = 2;

export type NumericInputConstraint = {
  min: number;
  max: number;
  decimal_places: number;
};

const DEFAULT_AREA_CONSTRAINT: NumericInputConstraint = {
  min: AREA_MIN_VALUE,
  max: AREA_MAX_VALUE,
  decimal_places: AREA_MAX_FRACTION_DIGITS,
};

export const formatConstraintNumber = (
  value: number,
  decimalPlaces = 2,
  options: { useGrouping?: boolean } = {},
): string =>
  new Intl.NumberFormat("ru-RU", {
    maximumFractionDigits: decimalPlaces,
    useGrouping: options.useGrouping ?? true,
  })
    .format(value)
    .replace(/[\u00a0\u202f]/g, " ");

const getFractionDigits = (value: string): number =>
  value.split(".")[1]?.length || 0;

const formatFractionDigits = (value: number): string => {
  const mod100 = value % 100;
  const mod10 = value % 10;
  const suffix = mod100 >= 11 && mod100 <= 14 ? "знаков" : mod10 === 1 ? "знака" : "знаков";
  return `${value} ${suffix}`;
};

export const normalizeAreaInput = (value: unknown): string => {
  const compactValue = String(value ?? "")
    .replace(/\s/g, "")
    .replace(/,/g, ".");
  const isNegative = compactValue.startsWith("-");
  const [integerPart = "", ...fractionParts] = compactValue
    .replace(/[^\d.]/g, "")
    .split(".");
  const normalizedIntegerPart =
    integerPart || (fractionParts.length ? "0" : "");
  const sign = isNegative ? "-" : "";

  if (!fractionParts.length) return `${sign}${normalizedIntegerPart}`;

  return `${sign}${normalizedIntegerPart}.${fractionParts.join("")}`;
};

export const finalizeAreaInput = (value: unknown): string => {
  const normalizedValue = normalizeAreaInput(value);
  return normalizedValue.endsWith(".")
    ? normalizedValue.slice(0, -1)
    : normalizedValue;
};

// Form state and API payloads use a dot, while marketplace inputs use the Russian decimal separator.
export const formatDecimalInput = (value: unknown): string =>
  normalizeAreaInput(value).replace(".", ",");

export const getNumericInputError = (
  value: unknown,
  constraint: NumericInputConstraint,
  options: {
    required?: boolean;
    requiredMessage?: string;
    minMessage?: (formattedValue: string) => string;
    maxMessage?: (formattedValue: string) => string;
    unit?: string;
    valueName?: string;
    useGrouping?: boolean;
  } = {},
): string | null => {
  const normalizedValue = normalizeAreaInput(value);
  const unitSuffix = options.unit ? ` ${options.unit}` : "";
  const valueName = options.valueName || "Значение";

  if (!normalizedValue) {
    return options.required
      ? options.requiredMessage || `Укажите ${valueName.toLowerCase()}.`
      : null;
  }

  if (normalizedValue === "-") {
    return "Введите числовое значение.";
  }

  const parsedValue = Number(normalizedValue);
  if (!Number.isFinite(parsedValue)) {
    return "Введите числовое значение.";
  }
  if (parsedValue < constraint.min) {
    const formattedValue = `${formatConstraintNumber(constraint.min, constraint.decimal_places, {
      useGrouping: options.useGrouping,
    })}${unitSuffix}`;
    return (
      options.minMessage?.(formattedValue) ||
      `${valueName} не может быть меньше ${formattedValue}.`
    );
  }
  if (parsedValue > constraint.max) {
    const formattedValue = `${formatConstraintNumber(constraint.max, constraint.decimal_places, {
      useGrouping: options.useGrouping,
    })}${unitSuffix}`;
    return (
      options.maxMessage?.(formattedValue) ||
      `${valueName} не может быть больше ${formattedValue}.`
    );
  }

  if (getFractionDigits(normalizedValue) > constraint.decimal_places) {
    return constraint.decimal_places
      ? `Допустимо не более ${formatFractionDigits(constraint.decimal_places)} после запятой.`
      : "Допустимы только целые значения.";
  }

  return null;
};

export const getNumericConstraintHint = (
  constraint: NumericInputConstraint,
  unit = "",
  options: { useGrouping?: boolean } = {},
): string => {
  const unitSuffix = unit ? ` ${unit}` : "";
  const precisionHint = constraint.decimal_places
    ? `до ${formatFractionDigits(constraint.decimal_places)} после запятой`
    : "только целые значения";

  return `От ${formatConstraintNumber(constraint.min, constraint.decimal_places, options)} до ${formatConstraintNumber(
    constraint.max,
    constraint.decimal_places,
    options,
  )}${unitSuffix} · ${precisionHint}`;
};

export const getAreaInputError = (
  value: unknown,
  required = false,
  constraint: NumericInputConstraint = DEFAULT_AREA_CONSTRAINT,
  unit = "м²",
): string | null =>
  getNumericInputError(value, constraint, {
    required,
    requiredMessage: "Укажите площадь.",
    minMessage: (formattedValue) => `Минимальная площадь — ${formattedValue}.`,
    maxMessage: (formattedValue) => `Максимальная площадь — ${formattedValue}.`,
    unit,
    valueName: "Площадь",
  });
